import {
  Component,
  Inject,
  Input,
  OnChanges,
  OnDestroy, Optional,
  SimpleChanges,
  TemplateRef,
  ViewChild
} from "@angular/core";
import {finalize} from "rxjs/operators";
import Pagination from "../../components/pagination/pagination";
import {ITableHeader} from "../../../interfaces/table-header";
import {TableEntityExtensionService} from "./table-entity-extension.service";
import {LoaderService} from "../../services/loader.service";
import {ActivatedRoute} from "@angular/router";
import {CardService} from "../../components/card/card.service";
import {Subscription} from "rxjs";

@Component({
  template: ''
})
export class TableEntityExtension implements OnChanges, OnDestroy {

  footerTmp: TemplateRef<any>;

  @ViewChild('actions') actionsTmp: TemplateRef<any>;
  @ViewChild('footer') set footerTmp$ (tmp: TemplateRef<any>) {
    this.footerTmp = tmp;

    if (this.cardService && this.footerTmp) {
      this.cardService.setPortal('footer', this.footerTmp);
    }
  }

  @Input() addButton: boolean = false;
  @Input() relativePath: boolean = false;

  @Input('account') accountData: any;
  @Input('user') userData: any;
  @Input('product') productData: any;
  @Input('storeCollection') storeCollectionData: any;
  @Input('project') projectData: any;
  @Input('store') storeData: any;

  headers: ITableHeader[] = [];
  entityPath: string[] = [];
  createPath: string[] = [];
  editPath: string[] = [];
  isEntity: boolean;

  query: any = {
    filters: {},
    order: null
  };
  source: any;
  forms: any = {};
  pagination: Pagination = new Pagination();

  changes: any[] = [];

  updateHook$: Subscription;

  constructor(@Inject('modelService') protected modelService: any,
              @Optional() protected cardService: CardService,
              protected loaderService: LoaderService,
              protected tableEntityExtensionService: TableEntityExtensionService,
              protected route: ActivatedRoute) {
    this.query.pagination = this.pagination.getQuery();

    if (this.cardService) {
      this.cardService.refresher.show();
      this.updateHook$ = this.cardService.refresher.updateChanges.subscribe(() => {
        this.getItems();
      });
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if ('addButton' in changes) {
      setTimeout(() => {
        if (this.cardService && changes.addButton.currentValue && this.actionsTmp) {
          this.cardService.setPortal('actions', this.actionsTmp);
        }
      });
    }

    this.changes.push(changes);
    this.checkChanges();
  }

  checkChanges() {
    this.changes.forEach((changes) => {
      // Headers
      if ('accountData' in changes && changes.accountData.currentValue) {
        this.handleHeaders(changes.accountData.currentValue, 'account', 'account');
      }

      if ('userData' in changes && changes.userData.currentValue) {
        this.handleHeaders(changes.userData.currentValue, 'user', 'userID');
      }

      if ('productData' in changes && changes.productData.currentValue) {
        this.handleHeaders(changes.productData.currentValue, 'product', 'product');
      }

      if ('storeCollectionData' in changes && changes.storeCollectionData.currentValue) {
        this.handleHeaders(changes.storeCollectionData.currentValue, 'storeCollection', 'storeCollections');
      }

      if ('projectData' in changes && changes.projectData.currentValue) {
        this.handleHeaders(changes.projectData.currentValue, 'project', 'projects');
      }

      if ('storeData' in changes && changes.storeData.currentValue) {
        this.handleHeaders(changes.storeData.currentValue, 'store', 'store');
      }
    })
  }

  ngOnDestroy() {
    if (this.cardService) {
      if (this.addButton && this.actionsTmp) {
        this.cardService.clearPortal('actions');
      }

      if (this.footerTmp) {
        this.cardService.clearPortal('footer');
      }

      this.cardService.refresher.hide();
    }

    if (this.updateHook$) {
      this.updateHook$.unsubscribe();
    }
  }

  routesInit(type, link = type) {
    this.isEntity = this.tableEntityExtensionService.routerService.isEntity(type, this.route);

    this.createPath = this.tableEntityExtensionService.routerService.getPath(['/', link, 'create'], this.relativePath, this.isEntity);
    this.entityPath = this.tableEntityExtensionService.routerService.getPath(['/', link, 'view', ':id'], this.relativePath, this.isEntity);
    this.editPath = this.tableEntityExtensionService.routerService.getPath(['/', link, 'edit', ':id'], this.relativePath, this.isEntity);
  }

  setPage(event: any): void {
    this.pagination.setPagination(event);
    this.query.pagination = this.pagination.getQuery();
    this.getItems();
  }

  getItems(): void {
    this.loaderService.show();
    this.modelService.index(this.query)
      .subscribe((res: any) => {
          this.source = res.array;
          this.pagination.fullCount = res.count;
          this.loaderService.hide();
        },
        err => {
          this.loaderService.hide();
        }
      );
  }

  delete(item, id) {
    this.tableEntityExtensionService.deleteService.process(item.type, item.name).then(() => {
      this.modelService.delete(id).subscribe(() => {
        this.getItems();
      });
    });
  }

  save(item, index, data?) {
    const form = this.forms[item.id];
    if (this.tableEntityExtensionService.validateService.validate(form)) {
      const values = {...form.getRawValue(), ...data};
      form.disable();
      this.modelService.update(Object.assign({id: item.id}, values)).pipe(
        finalize(() => form.enable())
      ).subscribe((result) => {
        Object.assign(item, result);
        this.tableEntityExtensionService.formService.setDefaultValues(form);
      });
    } else {
      this.tableEntityExtensionService.toastrService.warning(this.tableEntityExtensionService.messageService.incorrectField, 'Warning');
    }
  }

  handleHeaders(value, field, key) {
    this.tableEntityExtensionService.tableHeaderService.remove(this.headers, key);
    this.query.filters[field] = parseInt(value);
  }

  onSearch(header: ITableHeader) {
    if (header.filter.value === false || header.filter.value) {
      if (header.filter.equals) {
        this.query.filters[header.filter.key] = {
          equals: header.filter.value
        }
      } else {
        this.query.filters[header.filter.key] = header.filter.value;
      }
    } else {
      delete this.query.filters[header.filter.key];
    }
    this.getItems();
  }

  onSort(header: ITableHeader) {
    this.query.order = {};
    if (header.sort?.direction) {
      this.query.order[header.sort.key] = (header.sort.direction === 1) ? 'ASC' : 'DESC';
    } else {
      this.query.order = null;
    }
    this.getItems();
  }
}
